import React from "react";
import { Entity } from "@common/types/entity";
import { Field } from "@design-system/Field/Field";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { Loader } from "@design-system/Loader/Loader";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { PlusIcon } from "@icons";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import {
    isSenderAvailableForEntity,
    SenderOption,
} from "./useNativeInvoiceSender";
import { useNativeInvoiceGmailConnect } from "./NativeInvoiceGmailConnect";
import styles from "./SidePanel.module.scss";

interface Props {
    label?: string;
    entities: Entity[];
    selectedEntity?: Entity;
    onSelectEntity: (entity: Entity | undefined) => void;
    // When omitted, the control is entity-only (accrual importer). On the
    // invoice, every entity is listed once per sending address so one pick
    // sets both.
    senderOptions?: SenderOption[];
    selectedSenderValue?: string;
    onSelectSender?: (senderValue: string) => void;
    placeholder?: string;
    labelRight?: React.ReactNode;
    // The sending entity is immutable once an invoice exists (it drives
    // accounting/JE posting and branding), or when catalog items on the
    // invoice are scoped to the selected entity. Sender addresses stay
    // choosable; only the entity-only (no senders) control is disabled.
    isEntityLocked?: boolean;
    entityLockedTooltip?: string;
    isReviewHighlighted?: boolean;
}

// Entity ids are numeric and sender values are "kick" or a UUID, so ":" can
// never appear in either half.
function toOptionValue(entity: Entity, senderValue: string): string {
    return `${entity.id}:${senderValue}`;
}

function renderFromEntityValue(
    entity: Entity | undefined,
    senderEmail?: string,
) {
    if (!entity) {
        return null;
    }

    return (
        <div className={styles.fromOption}>
            <EntityAvatar entity={entity} size="xs" aria-hidden />
            <span className={styles.fromOptionName}>{entity.name}</span>
            {senderEmail && (
                <span className={styles.fromOptionEmail}>{senderEmail}</span>
            )}
        </div>
    );
}

// The invoice sender ("From") is always one of the workspace's own business
// entities; each entity is listed once per selectable sending address, so one
// pick sets both the entity and the address the email goes out from. When the
// entity is locked, only that entity's sending addresses stay in the list.
export const FromEntitySelect: React.FC<Props> = ({
    label,
    entities,
    selectedEntity,
    onSelectEntity,
    senderOptions = [],
    selectedSenderValue = "",
    onSelectSender,
    placeholder = "Select business",
    labelRight,
    isEntityLocked,
    entityLockedTooltip,
    isReviewHighlighted,
}) => {
    const selectableEntities =
        isEntityLocked && selectedEntity ? [selectedEntity] : entities;
    const hasSenders = senderOptions.length > 0;
    const isPickerDisabled = Boolean(isEntityLocked && !hasSenders);
    const selectedSender = senderOptions.find(
        (option) => option.value === selectedSenderValue,
    );
    const gmailConnect = useNativeInvoiceGmailConnect();
    const connectGmailFooter = hasSenders ? gmailConnect : undefined;

    let selectedOptionValue: string | null = null;
    if (selectedEntity) {
        selectedOptionValue = hasSenders
            ? toOptionValue(selectedEntity, selectedSenderValue)
            : selectedEntity.id.toString();
    }

    const items: Array<PickerItemData<string>> = hasSenders
        ? selectableEntities.flatMap((entity) =>
              senderOptions
                  .filter((sender) =>
                      isSenderAvailableForEntity(
                          sender.entityUuids,
                          entity.uuid,
                      ),
                  )
                  .map((sender) => ({
                      value: toOptionValue(entity, sender.value),
                      label: entity.name ?? "",
                      leading: (
                          <EntityAvatar entity={entity} size="xs" aria-hidden />
                      ),
                      labelHint: sender.email,
                  })),
          )
        : selectableEntities.map((entity) => ({
              value: entity.id.toString(),
              label: entity.name ?? "",
              leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
          }));

    const picker = (
        <Picker.Select
            items={items}
            value={selectedOptionValue}
            onValueChange={(value) => {
                if (value === null) {
                    return;
                }
                if (!hasSenders) {
                    const entity = entities.find(
                        (item) => item.id.toString() === value,
                    );
                    if (entity && entity.id !== selectedEntity?.id) {
                        onSelectEntity(entity);
                    }
                    return;
                }
                const separatorIndex = value.indexOf(":");
                const entityId = value.slice(0, separatorIndex);
                const senderValue = value.slice(separatorIndex + 1);
                const entity = entities.find(
                    (item) => item.id.toString() === entityId,
                );
                if (!entity) {
                    return;
                }
                if (
                    entity.id !== selectedEntity?.id &&
                    !(isEntityLocked && selectedEntity)
                ) {
                    onSelectEntity(entity);
                }
                if (senderValue !== selectedSenderValue && onSelectSender) {
                    onSelectSender(senderValue);
                }
            }}
            disabled={isPickerDisabled}
            placeholder={placeholder}
            renderValue={() =>
                renderFromEntityValue(selectedEntity, selectedSender?.email)
            }
            trigger={
                <Picker.Button
                    variant="primary"
                    size="xl"
                    block
                    aria-label={label ?? "Entity"}
                    className={
                        isReviewHighlighted
                            ? styles.reviewTaskHighlight
                            : undefined
                    }
                />
            }
            renderFooter={
                connectGmailFooter
                    ? () => (
                          <Picker.Item
                              leading={
                                  connectGmailFooter.isConnecting ? (
                                      <Loader inline />
                                  ) : (
                                      <PlusIcon />
                                  )
                              }
                              disabled={connectGmailFooter.isConnecting}
                              onSelect={connectGmailFooter.connect}
                          >
                              Connect Gmail
                          </Picker.Item>
                      )
                    : undefined
            }
        />
    );
    const entitySelect = (
        <Tooltip content={isPickerDisabled ? entityLockedTooltip : undefined}>
            {picker}
        </Tooltip>
    );

    if (labelRight) {
        return (
            <Field.Root>
                <div className={styles.entityLabelRow}>
                    {label ? (
                        <Field.Label nativeLabel={false} render={<span />}>
                            {label}
                        </Field.Label>
                    ) : null}
                    {labelRight}
                </div>
                {entitySelect}
            </Field.Root>
        );
    }

    if (label) {
        return (
            <Field label={label} control="button">
                {entitySelect}
            </Field>
        );
    }

    return <Field.Root>{entitySelect}</Field.Root>;
};
